import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Search, Plus, Check, X, Layers, ArrowRight, Loader2, Sparkles } from "lucide-react";
import { GuestCheckoutDialog } from "@/components/GuestCheckoutDialog";

export default function BundleBuilder() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState([]);
  const [pricing, setPricing] = useState({ bundle_size: 3, bundle_price: 9.95, single_form_price: 3.95 });
  const [busy, setBusy] = useState(false);
  const [guestOpen, setGuestOpen] = useState(false);
  const [suggestions, setSuggestions] = useState([]);

  useEffect(() => { api.get("/plans").then(({ data }) => setPricing((p) => ({ ...p, ...data }))).catch(() => {}); }, []);
  useEffect(() => {
    const adds = sp.getAll("add").filter(Boolean).slice(0, pricing.bundle_size || 3);
    if (adds.length === 0) return;
    Promise.all(adds.map((n) => api.get(`/forms/${n}`).then(({ data }) => data).catch(() => null)))
      .then((forms) => {
        const valid = forms.filter((f) => f && f.number);
        if (valid.length) setSelected((s) => {
          const merged = [...s];
          valid.forEach((f) => { if (!merged.some((x) => x.number === f.number)) merged.push({ number: f.number, title: f.title }); });
          return merged.slice(0, pricing.bundle_size || 3);
        });
      });
  }, [sp, pricing.bundle_size]);
  useEffect(() => { const t = setTimeout(() => setDebouncedQ(q), 350); return () => clearTimeout(t); }, [q]);
  useEffect(() => {
    setLoading(true);
    api.get("/forms", { params: { q: debouncedQ || undefined, source: "court", page_size: 12 } })
      .then(({ data }) => setResults(data.items || [])).finally(() => setLoading(false));
  }, [debouncedQ]);

  const size = pricing.bundle_size || 3;
  const isSelected = (n) => selected.some((s) => s.number === n);

  // Smart companion suggestions based on the most recently added form.
  useEffect(() => {
    if (selected.length === 0 || selected.length >= size) { setSuggestions([]); return; }
    const latest = selected[0].number;
    let cancelled = false;
    api.get(`/forms/${latest}/related`).then(({ data }) => {
      if (cancelled) return;
      const recs = (data.related || []).filter((r) => !isSelected(r.number)).slice(0, 4);
      setSuggestions(recs);
    }).catch(() => setSuggestions([]));
    return () => { cancelled = true; };
  }, [selected, size]);
  const toggle = (f) => {
    if (isSelected(f.number)) {
      setSelected((s) => s.filter((x) => x.number !== f.number));
      toast(`Removed — ${Math.max(0, selected.length - 1)}/${size} in your bundle`);
      return;
    }
    if (selected.length >= size) { toast.error(`A bundle holds ${size} forms. Remove one first.`); return; }
    const next = selected.length + 1;
    setSelected((s) => [...s, { number: f.number, title: f.title }]);
    if (next === size) toast.success("Bundle complete — ready to checkout! 🎉");
    else toast.success(`Added to your bundle (${next}/${size})`);
  };

  const fullPrice = (pricing.single_form_price || 3.95) * size;
  const savings = Math.max(0, fullPrice - (pricing.bundle_price || 9.95));

  const checkout = async () => {
    if (selected.length !== size) { toast.error(`Pick ${size} forms to build your bundle.`); return; }
    if (!user) { setGuestOpen(true); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout-bundle", {
        form_numbers: selected.map((s) => s.number), origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not start checkout");
      setBusy(false);
    }
  };

  const guestCheckout = async (email) => {
    try {
      const { data } = await api.post("/payments/guest/checkout-bundle", {
        email, accepted_terms: true, form_numbers: selected.map((s) => s.number), origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not start checkout");
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>{`Build a Form Bundle — Any ${size} NC Forms for $${pricing.bundle_price} | NC Legal Forms`}</title>
        <meta name="description" content={`Pick any ${size} North Carolina court forms and unlock them together for $${pricing.bundle_price} — a discount on the per-form price.`} />
      </Helmet>
      <Navbar />
      <section className="max-w-7xl mx-auto px-6 pt-16 pb-8">
        <p className="overline text-accent mb-3 flex items-center gap-2"><Layers size={14} /> Bundle &amp; save</p>
        <h1 className="font-serif font-bold text-5xl sm:text-6xl tracking-tighter mb-4">Build your form bundle</h1>
        <p className="text-muted-foreground max-w-2xl">
          Need a few related documents? Pick any <span className="font-semibold text-foreground">{size} forms</span> and unlock them together for
          <span className="font-semibold text-foreground"> ${pricing.bundle_price}</span>
          {savings > 0 && <> — that's <span className="text-accent font-semibold">${savings.toFixed(2)} off</span> the ${fullPrice.toFixed(2)} you'd pay per form.</>}
        </p>
      </section>

      <section className="max-w-7xl mx-auto px-6 pb-24 grid lg:grid-cols-3 gap-8">
        {/* Search + results */}
        <div className="lg:col-span-2">
          <div className="relative mb-5">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
            <Input value={q} onChange={(e) => setQ(e.target.value)} data-testid="bundle-search"
              placeholder="Search forms by name or number (e.g. divorce, AOC-CV-100)" className="rounded-sm h-12 pl-10" />
          </div>
          {suggestions.length > 0 && (
            <div className="mb-5 border-2 border-accent/30 bg-accent/5 p-4" data-testid="bundle-suggestions">
              <p className="overline text-accent text-[0.6rem] mb-3 flex items-center gap-1.5"><Sparkles size={13} /> Often filed together</p>
              <div className="flex flex-wrap gap-2">
                {suggestions.map((r) => (
                  <button key={r.number} onClick={() => toggle(r)} data-testid={`bundle-suggest-${r.number}`}
                    title={r.reason || ""}
                    className="inline-flex items-center gap-2 pl-3 pr-2 py-1.5 bg-background border-2 border-black/15 hover:border-accent transition-colors text-sm">
                    <span className="min-w-0 max-w-[220px] truncate">{r.title}</span>
                    <span className="text-[0.7rem] text-muted-foreground font-mono">{r.number}</span>
                    <span className="grid place-items-center w-5 h-5 bg-accent text-accent-foreground shrink-0"><Plus size={13} /></span>
                  </button>
                ))}
              </div>
            </div>
          )}
          {loading ? (
            <p className="text-muted-foreground py-12 text-center animate-pulse">Loading forms…</p>
          ) : (
            <div className="grid sm:grid-cols-2 gap-3" data-testid="bundle-results">
              {results.map((f) => {
                const sel = isSelected(f.number);
                return (
                  <button key={f.number} onClick={() => toggle(f)} data-testid={`bundle-pick-${f.number}`}
                    className={`text-left p-4 border-2 transition-colors ${sel ? "border-accent bg-accent/5" : "border-black/10 hover:border-black/30"}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-sm font-medium leading-snug line-clamp-2">{f.title}</p>
                        <p className="text-xs text-muted-foreground mt-1 font-mono">{f.number}</p>
                      </div>
                      <span className={`grid place-items-center w-7 h-7 shrink-0 ${sel ? "bg-accent text-accent-foreground" : "bg-secondary text-muted-foreground"}`}>
                        {sel ? <Check size={15} /> : <Plus size={15} />}
                      </span>
                    </div>
                  </button>
                );
              })}
              {results.length === 0 && <p className="col-span-2 text-sm text-muted-foreground py-8 text-center">No forms match that search.</p>}
            </div>
          )}
        </div>

        {/* Bundle cart */}
        <div className="lg:col-span-1">
          <div className="border-2 border-black p-6 sticky top-24" data-testid="bundle-cart">
            <div className="flex items-baseline justify-between mb-1">
              <h2 className="font-serif text-2xl">Your bundle</h2>
              <span className="text-sm text-muted-foreground" data-testid="bundle-count">{selected.length}/{size}</span>
            </div>
            <p className="text-sm text-muted-foreground mb-5">Any {size} forms for one flat price.</p>
            <div className="flex items-center gap-1.5 mb-5" data-testid="bundle-progress" aria-label={`${selected.length} of ${size} forms selected`}>
              {Array.from({ length: size }).map((_, i) => {
                const filled = i < selected.length;
                return (
                  <div key={i} className="flex items-center gap-1.5 flex-1">
                    <span data-testid={`bundle-step-${i + 1}`}
                      className={`grid place-items-center w-6 h-6 shrink-0 text-xs font-bold border-2 transition-colors duration-300 ${filled ? "bg-accent text-accent-foreground border-accent" : "bg-background text-muted-foreground border-black/15"}`}>
                      {filled ? <Check size={13} /> : i + 1}
                    </span>
                    {i < size - 1 && <span className={`h-0.5 flex-1 rounded-full transition-colors duration-300 ${i < selected.length - 1 ? "bg-accent" : "bg-black/10"}`} />}
                  </div>
                );
              })}
            </div>
            <div className="space-y-2 mb-5">
              {Array.from({ length: size }).map((_, i) => {
                const s = selected[i];
                return (
                  <div key={i} className={`flex items-center gap-2 p-3 border-2 text-sm ${s ? "border-accent/40 bg-accent/5" : "border-dashed border-black/15"}`}>
                    {s ? (
                      <>
                        <span className="min-w-0 flex-1">
                          <span className="block truncate">{s.title}</span>
                          <span className="block text-xs text-muted-foreground font-mono">{s.number}</span>
                        </span>
                        <button onClick={() => toggle(s)} data-testid={`bundle-remove-${s.number}`}
                          className="grid place-items-center w-6 h-6 text-muted-foreground hover:text-[#E63946]"><X size={14} /></button>
                      </>
                    ) : (
                      <span className="text-muted-foreground/60">Slot {i + 1} — add a form</span>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="flex items-end justify-between border-t border-border pt-4 mb-5">
              <div>
                <p className="font-serif text-4xl">${pricing.bundle_price}</p>
                {savings > 0 && <p className="text-xs text-accent font-semibold">Save ${savings.toFixed(2)}</p>}
              </div>
              <p className="text-xs text-muted-foreground text-right">one-time<br />no subscription</p>
            </div>
            <Button onClick={checkout} disabled={busy || selected.length !== size} data-testid="bundle-checkout-btn"
              className="w-full rounded-sm h-12 gap-2 bg-accent text-accent-foreground hover:bg-primary hover:text-primary-foreground transition-colors">
              {busy ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} />}
              {selected.length === size ? `Unlock these ${size} forms` : `Pick ${size - selected.length} more`}
            </Button>
            {!user && <p className="text-xs text-muted-foreground text-center mt-3">No account needed — check out as a guest.</p>}
          </div>
        </div>
      </section>
      <GuestCheckoutDialog open={guestOpen} onOpenChange={setGuestOpen}
        title="Get your 3-form bundle" priceLabel={`$${pricing.bundle_price}`} onSubmit={guestCheckout} />
      <Footer />
    </div>
  );
}
